import React, { useState, useEffect } from "react";
import { ChartBarIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import RoleOnly from "../../../routes/roleOnly";
import {
  fetchPhoneroStats,
  refreshPhoneroStats,
  initializePhoneroStats,
  clearPhoneroStats,
  getPhoneroStatsStatus,
  syncPhoneroCalls,
  startPhoneroSyncScheduler,
  stopPhoneroSyncScheduler,
  getPhoneroSyncSchedulerStatus,
  syncPhoneroCommunications,
  startPhoneroStatsSyncScheduler,
  stopPhoneroStatsSyncScheduler,
  getPhoneroStatsSyncSchedulerStatus,
} from "../../../api/company";
import { toast } from "react-hot-toast";

const PhoneroSettings = ({ companyId }) => {
  const { t } = useTranslation();

  const [initializing, setInitializing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingCommunications, setSyncingCommunications] = useState(false);
  const [syncSchedulerLoading, setSyncSchedulerLoading] = useState(false);
  const [statsSyncSchedulerLoading, setStatsSyncSchedulerLoading] =
    useState(false);
  const [pendingJobs, setPendingJobs] = useState(0);
  const [jobStatus, setJobStatus] = useState(null);
  const [syncSchedulerStatus, setSyncSchedulerStatus] = useState(null);
  const [statsSyncSchedulerStatus, setStatsSyncSchedulerStatus] =
    useState(null);

  useEffect(() => {
    if (companyId) {
      loadStats();
      checkJobStatus();
      loadSyncSchedulerStatus();
      loadStatsSyncSchedulerStatus();
    }
  }, [companyId]);

  // Check job status periodically when there are pending jobs
  useEffect(() => {
    let interval;
    if (pendingJobs > 0) {
      interval = setInterval(() => {
        checkJobStatus();
      }, 5000); // Check every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pendingJobs]);

  const loadStats = async () => {
    try {
      const response = await fetchPhoneroStats();

      if (response.data && response.data.success) {
        const initialized = response.data.initialized;
        setIsInitialized(initialized);
      }
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const handleInitializeStats = async () => {
    setInitializing(true);

    try {
      const response = await initializePhoneroStats();

      if (response.data && response.data.success) {
        // Check job status immediately after starting
        setTimeout(() => {
          checkJobStatus();
          // In development (inline), also reload stats after a short delay
          setTimeout(() => {
            loadStats();
          }, 2000);
        }, 1000);
      }
    } catch (err) {
      console.error("Error initializing stats:", err);
    } finally {
      setInitializing(false);
    }
  };

  const handleClearStats = async () => {
    setClearing(true);

    try {
      const response = await clearPhoneroStats();

      if (response.data && response.data.success) {
        // Reload stats to show the cleared state
        loadStats();
      }
    } catch (err) {
      console.error("Error clearing stats:", err);
    } finally {
      setClearing(false);
    }
  };

  const handleSyncCalls = async () => {
    setSyncing(true);

    try {
      const response = await syncPhoneroCalls(50);

      if (response.data && response.data.success) {
        // Reload stats after syncing
        loadStats();
      }
    } catch (err) {
      console.error("Error syncing calls:", err);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncCommunications = async () => {
    setSyncingCommunications(true);
    try {
      const response = await syncPhoneroCommunications();
      if (response.data && response.data.success) {
        toast.success("Communication sync started successfully!");
      } else {
        toast.error(
          response.data?.message || "Failed to start communication sync"
        );
      }
    } catch (err) {
      console.error("Error syncing communications:", err);
      toast.error("Failed to sync communications");
    } finally {
      setSyncingCommunications(false);
    }
  };

  const handleStartSyncScheduler = async () => {
    setSyncSchedulerLoading(true);
    try {
      const response = await startPhoneroSyncScheduler();
      if (response.data && response.data.success) {
        toast.success("Sync scheduler started successfully!");
        // Refresh the status from the server since start response only has status field
        await loadSyncSchedulerStatus();
      } else {
        toast.error(response.data?.message || "Failed to start sync scheduler");
      }
    } catch (err) {
      console.error("Error starting sync scheduler:", err);
      toast.error("Failed to start sync scheduler");
    } finally {
      setSyncSchedulerLoading(false);
    }
  };

  const handleStopSyncScheduler = async () => {
    setSyncSchedulerLoading(true);
    try {
      const response = await stopPhoneroSyncScheduler();
      if (response.data && response.data.success) {
        toast.success("Sync scheduler stopped successfully!");
        // Refresh the status from the server since stop response only has status field
        await loadSyncSchedulerStatus();
      } else {
        toast.error(response.data?.message || "Failed to stop sync scheduler");
      }
    } catch (err) {
      console.error("Error stopping sync scheduler:", err);
      toast.error("Failed to stop sync scheduler");
    } finally {
      setSyncSchedulerLoading(false);
    }
  };

  const checkJobStatus = async () => {
    try {
      const response = await getPhoneroStatsStatus();

      if (response.data && response.data.success) {
        setJobStatus(response.data.job_status);
        setPendingJobs(response.data.pending_jobs || 0);
      }
    } catch (err) {
      console.error("Error checking job status:", err);
    }
  };

  const loadSyncSchedulerStatus = async () => {
    try {
      const response = await getPhoneroSyncSchedulerStatus();
      if (response.data && response.data.success) {
        setSyncSchedulerStatus({
          running: response.data.running,
          status: response.data.status,
          started_at: response.data.started_at,
          stopped_at: response.data.stopped_at,
          job_id: response.data.job_id,
        });
      }
    } catch (err) {
      console.error("Error loading sync scheduler status:", err);
    }
  };

  const loadStatsSyncSchedulerStatus = async () => {
    try {
      const response = await getPhoneroStatsSyncSchedulerStatus();
      if (response.data && response.data.success) {
        setStatsSyncSchedulerStatus({
          running: response.data.running,
          status: response.data.status,
          started_at: response.data.started_at,
          stopped_at: response.data.stopped_at,
          job_id: response.data.job_id,
        });
      }
    } catch (err) {
      console.error("Error loading stats sync scheduler status:", err);
    }
  };

  const handleStartStatsSyncScheduler = async () => {
    setStatsSyncSchedulerLoading(true);
    try {
      const response = await startPhoneroStatsSyncScheduler();
      if (response.data && response.data.success) {
        toast.success("Stats sync scheduler started successfully!");
        // Refresh the status from the server since start response only has status field
        await loadStatsSyncSchedulerStatus();
      } else {
        toast.error(
          response.data?.message || "Failed to start stats sync scheduler"
        );
      }
    } catch (err) {
      console.error("Error starting stats sync scheduler:", err);
      toast.error("Failed to start stats sync scheduler");
    } finally {
      setStatsSyncSchedulerLoading(false);
    }
  };

  const handleStopStatsSyncScheduler = async () => {
    setStatsSyncSchedulerLoading(true);
    try {
      const response = await stopPhoneroStatsSyncScheduler();
      if (response.data && response.data.success) {
        toast.success("Stats sync scheduler stopped successfully!");
        // Refresh the status from the server since stop response only has status field
        await loadStatsSyncSchedulerStatus();
      } else {
        toast.error(
          response.data?.message || "Failed to stop stats sync scheduler"
        );
      }
    } catch (err) {
      console.error("Error stopping stats sync scheduler:", err);
      toast.error("Failed to stop stats sync scheduler");
    } finally {
      setStatsSyncSchedulerLoading(false);
    }
  };

  return (
    <RoleOnly role="phone_settings" action="settings">
      <div className="space-y-6">
        {/* Initialization Notice */}
        {!isInitialized && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Statistics not initialized
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Historical call statistics need to be built. This will fetch
                    data from the last month in 2-day chunks to work around API
                    limits.
                  </p>
                  {pendingJobs > 0 && (
                    <p className="mt-2 font-medium">
                      Background job in progress... ({pendingJobs} pending)
                    </p>
                  )}
                  {jobStatus?.queue_adapter === "inline" && (
                    <p className="mt-1 text-xs text-yellow-600">
                      Development mode: Jobs run immediately
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleInitializeStats}
                    disabled={initializing || pendingJobs > 0}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                  >
                    {initializing ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Starting...
                      </>
                    ) : pendingJobs > 0 ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Processing...
                      </>
                    ) : (
                      "Initialize Statistics"
                    )}
                  </button>
                  <button
                    onClick={handleClearStats}
                    disabled={clearing || pendingJobs > 0}
                    className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {clearing ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Clearing...
                      </>
                    ) : (
                      "Clear Stats"
                    )}
                  </button>
                  <button
                    onClick={handleSyncCalls}
                    disabled={syncing}
                    className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-800 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {syncing ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Syncing...
                      </>
                    ) : (
                      "Sync Calls"
                    )}
                  </button>
                  <button
                    onClick={handleStartSyncScheduler}
                    disabled={
                      syncSchedulerLoading || syncSchedulerStatus?.running
                    }
                    className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-800 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {syncSchedulerLoading ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Starting...
                      </>
                    ) : (
                      "Start Auto-Sync"
                    )}
                  </button>
                  <button
                    onClick={handleStopSyncScheduler}
                    disabled={
                      syncSchedulerLoading || !syncSchedulerStatus?.running
                    }
                    className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {syncSchedulerLoading ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Stopping...
                      </>
                    ) : (
                      "Stop Auto-Sync"
                    )}
                  </button>
                  {syncSchedulerStatus && (
                    <div className="ml-4 text-sm text-gray-600">
                      Status:{" "}
                      <span
                        className={`font-medium ${
                          syncSchedulerStatus.running
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {syncSchedulerStatus.running ? "Running" : "Stopped"}
                      </span>
                      {syncSchedulerStatus.started_at && (
                        <span className="ml-2">
                          (Started:{" "}
                          {new Date(
                            syncSchedulerStatus.started_at
                          ).toLocaleString()}
                          )
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleSyncCommunications}
                    disabled={syncingCommunications}
                    className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-purple-800 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {syncingCommunications ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Syncing Communications...
                      </>
                    ) : (
                      "Sync Communications"
                    )}
                  </button>
                </div>

                {/* Stats Auto-Sync Section */}
                <div className="mt-4 pt-4 border-t border-yellow-200">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">
                    Stats Auto-Sync (Database Only)
                  </h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    Automatically refresh statistics every 10 minutes using
                    existing database data (no API calls).
                  </p>
                  <button
                    onClick={handleStartStatsSyncScheduler}
                    disabled={
                      statsSyncSchedulerLoading ||
                      statsSyncSchedulerStatus?.running
                    }
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-800 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {statsSyncSchedulerLoading ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Starting...
                      </>
                    ) : (
                      "Start Stats Auto-Sync"
                    )}
                  </button>
                  <button
                    onClick={handleStopStatsSyncScheduler}
                    disabled={
                      statsSyncSchedulerLoading ||
                      !statsSyncSchedulerStatus?.running
                    }
                    className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {statsSyncSchedulerLoading ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                        Stopping...
                      </>
                    ) : (
                      "Stop Stats Auto-Sync"
                    )}
                  </button>
                  {statsSyncSchedulerStatus && (
                    <div className="ml-4 text-sm text-gray-600">
                      Status:{" "}
                      <span
                        className={`font-medium ${
                          statsSyncSchedulerStatus.running
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {statsSyncSchedulerStatus.running
                          ? "Running"
                          : "Stopped"}
                      </span>
                      {statsSyncSchedulerStatus.started_at && (
                        <span className="ml-2">
                          (Started:{" "}
                          {new Date(
                            statsSyncSchedulerStatus.started_at
                          ).toLocaleString()}
                          )
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </RoleOnly>
  );
};

export default PhoneroSettings;
